export type RecognitionStatus = 'idle' | 'listening' | 'unsupported';

export interface RecognitionResult {
  transcript: string;
  confidence: number;
  isFinal: boolean;
}

interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<
    ArrayLike<{ transcript: string; confidence: number }> & { isFinal: boolean }
  >;
}

type RecognitionConstructor = new () => SpeechRecognitionLike;

function getConstructor(): RecognitionConstructor | null {
  const scope = globalThis as unknown as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null;
}

export const isRecognitionSupported = (): boolean => getConstructor() !== null;

export interface ListenOptions {
  lang?: string;
  signal?: AbortSignal;
  /** Fires on every interim and final chunk so callers can match mid-sentence. */
  onTranscript?: (transcript: string, isFinal: boolean) => void;
  /** Fires when recognition stops for a reason other than ordinary silence. */
  onError?: (code: string) => void;
}

/** Why listening stopped, in terms an actor can act on. */
export function recognitionErrorMessage(code: string): string {
  switch (code) {
    case 'aborted':
      return 'Listening stopped — another tab or app took the microphone.';
    case 'audio-capture':
      return 'No microphone was available. Another app may be holding it.';
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone blocked. Allow microphone access for this site, then start again.';
    case 'network':
      return 'Speech recognition needs an internet connection.';
    default:
      return `Listening stopped (${code}).`;
  }
}

/** A session that ends this fast is failing, not hearing silence. */
const RAPID_RESTART_MS = 300;
const MAX_RAPID_RESTARTS = 3;

/** Per-call state. Two listens overlap while the old mic tears down. */
interface ActiveRecognition {
  stopping: boolean;
  recognition: SpeechRecognitionLike | null;
}

/**
 * Thin wrapper over the (non-standard, Chromium/Safari-only) speech recognition
 * API. Every rehearsal mode treats a null result as "no opinion" so that an
 * unsupported browser simply falls back to manual Got It / Show Line.
 */
export class RecognitionSession {
  private current: ActiveRecognition | null = null;

  get supported(): boolean {
    return isRecognitionSupported();
  }

  /** Resolves with the best final transcript, or null when nothing was heard. */
  listen(options: ListenOptions = {}): Promise<string | null> {
    const Constructor = getConstructor();
    if (!Constructor) return Promise.resolve(null);

    const { lang = 'en-US', signal, onTranscript, onError } = options;

    return new Promise((resolve) => {
      let finals = '';
      let settled = false;
      let rapidRestarts = 0;
      let startedAt = 0;
      const session: ActiveRecognition = { stopping: false, recognition: null };
      this.current = session;

      const finish = (value: string | null) => {
        if (settled) return;
        settled = true;
        signal?.removeEventListener('abort', onAbort);
        // Only disown the mic if a newer listen has not already claimed it.
        if (this.current === session) this.current = null;
        resolve(value);
      };

      const onAbort = () => {
        session.stopping = true;
        session.recognition?.abort();
        finish(finals.trim() || null);
      };

      if (signal?.aborted) {
        finish(null);
        return;
      }
      signal?.addEventListener('abort', onAbort, { once: true });

      const start = () => {
        const recognition = new Constructor();
        session.recognition = recognition;

        recognition.lang = lang;
        // Continuous keeps the microphone open through the pauses in a speech.
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
          let interim = '';
          for (let i = event.resultIndex; i < event.results.length; i += 1) {
            const result = event.results[i];
            const alternative = result?.[0];
            if (!alternative) continue;
            if (result.isFinal) finals += ` ${alternative.transcript}`;
            else interim += ` ${alternative.transcript}`;
          }
          onTranscript?.(`${finals} ${interim}`.replace(/\s+/g, ' ').trim(), interim === '');
        };

        recognition.onerror = (event) => {
          // Silence is expected while the actor searches for the line.
          if (!session.stopping && event.error === 'no-speech') return;
          if (!session.stopping) onError?.(event.error);
          finish(finals.trim() || null);
        };

        recognition.onend = () => {
          if (session.stopping || signal?.aborted) {
            finish(finals.trim() || null);
            return;
          }
          rapidRestarts =
            performance.now() - startedAt < RAPID_RESTART_MS ? rapidRestarts + 1 : 0;
          if (rapidRestarts >= MAX_RAPID_RESTARTS) {
            finish(finals.trim() || null);
            return;
          }
          start();
        };

        startedAt = performance.now();
        try {
          recognition.start();
        } catch {
          finish(finals.trim() || null);
        }
      };

      start();
    });
  }

  stop(): void {
    const session = this.current;
    if (!session) return;
    session.stopping = true;
    session.recognition?.stop();
  }
}
